import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { useMediaQuery } from '@mui/material';
import { FAIcon, IconStyle, Tag, Theme, useBreakpoint } from '../../components';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import { authActions } from '../../../sdk/store/authActions';
import amwayLogo from '../../../assets/image/Amw_Logo_Black.webp';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
// import { ProfileAvatar, NotificationDrawer } from '../../app-components';
import { NotificationDrawer } from '../../app-components';
import { getAccessToken, isTokenValid, isEmpty, updateState, getState, getLoginToken } from '../../utils';
import { generateBreadcrumbs } from '../../utils/breadcrumb';
import styles from './Header.module.scss';

export const Header = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userInfo, token } = useAppSelector(state => state.auth);
  const [isLogon, setIsLogon] = useState<boolean>(false);
  const [isImpersonate, setIsImpersonate] = useState(false);
  const [impersonator, setImpersonator] = useState('');
  const isProd = useRef(window.location.hostname === 'portal.amwaynow.sg').current;
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const ipadDimensions = useMediaQuery('(max-width:960px) and (min-width:768px)');

  useEffect(() => {
    if (!token) {
      // Read from token
      const appToken = getAccessToken();
      if (appToken && isTokenValid(token)) {
        const decoded = jwtDecode(appToken);
        dispatch(authActions.setLogin({ userInfo: decoded, token: appToken, error: false, errorMessage: '' }));
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (userInfo && !isEmpty(userInfo)) {
        setIsLogon(true);
        let fromImpersonate = await getState('fromImpersonate');
        setIsImpersonate(fromImpersonate || false);
      }
    })();
  }, [userInfo]);

  useEffect(() => {
    if (isImpersonate) {
      const loginToken = getLoginToken() || '';
      const decoded: DynamicObjectInterface | null = loginToken ? jwtDecode(loginToken) : null;
      if (decoded) {
        setImpersonator(decoded.nativeid?.toUpperCase());
      }
    }
  }, [isImpersonate]);

  // const pathName = useMemo(() => window.location.pathname, []);
  // const loginPath = useMemo(() => process.env.REACT_APP_LOGIN_PATH ?? '/auth/login', []);
  const notificationComponent = useMemo(() => <NotificationDrawer />, []);

  const handleGoToImpersonate = useCallback(async () => {
    localStorage.removeItem('pinNumber');
    localStorage.removeItem('appToken');
    localStorage.removeItem('exp');
    localStorage.removeItem('userId');
    localStorage.removeItem('loginId');
    await updateState(['fromImpersonate', true]);
    dispatch(authActions.clearLogin());
  }, []);

  const [breadcrumbData, setBreadcrumbData] = useState<{ name: string; link: string }[]>([]);

  useEffect(() => {
    const pathName = window.location.pathname;
    if (!pathName.startsWith('/admin')) {
      const breadcrumbs = generateBreadcrumbs(pathName);
      setBreadcrumbData(breadcrumbs);
    }
  }, [window.location.pathname]);

  return (
    <header className={styles['page-header']} data-testid="framework-header">
      <div className={styles['left']}>
        {isDesktopView && (
          <img
            onClick={() => navigate('/')}
            className={styles['logo']}
            src={amwayLogo}
            alt="amway logo"
            height={!isDesktopView ? 30 : undefined}
          />
        )}
        <div className={styles['title']}></div>
      </div>
      <div className={styles['center']}>
        {userInfo?.frameworkRole === 'MEMBER' && isDesktopView && !ipadDimensions && (
          <Breadcrumb breadcrumbData={breadcrumbData} />
        )}
        {!isDesktopView && (
          <img
            onClick={() => navigate('/')}
            className={styles['logo']}
            src={amwayLogo}
            alt="amway logo"
            height={!isDesktopView ? 30 : undefined}
          />
        )}
      </div>

      <div className={styles['right']}>
        {isImpersonate && <Tag text={`Impersonator: ${impersonator}`} theme={Theme.ERROR} />}
        {/* <ChangeLanguage /> */}
        {isLogon && userInfo && !isDesktopView && (
          <>
            {/* <ProfileAvatar isImpersonate={isImpersonate} /> */}
            {userInfo?.frameworkRole !== 'ADMIN' ? notificationComponent : null}
          </>
        )}
        {!isProd && isLogon && userInfo?.frameworkRole === 'ADMIN' && (
          <Link
            to={'/auth/login'}
            onClick={() => handleGoToImpersonate()}
            className={styles['link']}
            title={t('framework.menu.impersonate')}
          >
            <FAIcon
              iconName="arrow-right-to-bracket"
              iconStyle={IconStyle.SOLID}
              className={`${styles['icon']} fa-lg`}
            />
          </Link>
        )}
      </div>
    </header>
  );
};
